import { Injectable } from '@angular/core';
import {
  WebLinkClient,
  EventClass,
  TaskSequence,
} from '../../assets/scripts/web-link-api';
import { LogService } from './logs.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TemplateService } from './template.service';
import { Snackbars } from '../models';
import { SnackbarComponent } from '../components';
import {
  MDEV_CONFIG,
  NRTKIOSK_CONFIG,
  TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLES_DYNAMICS,
} from '../constants/print';
import { environment } from 'projects/kiosk-app/src/environments/environment';
import { AuthService } from './auth.service';
import moment from 'moment';
import uniq from 'lodash/uniq';
import { LogSource, LogType } from '../models/common';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  public isDebugModeEnabled = false;
  private printUrl: string = '';
  private kioskId: any;
  private WebLink: any;
  private isConnected = false;
  public isNewPrintVersion = false;
  private Machines = {
    NRTKIOSK: NRTKIOSK_CONFIG,
    MDEV: MDEV_CONFIG,
  };
  private snackbar: Snackbars = {} as Snackbars;
  public isPrinting: boolean = false;

  constructor(
    private readonly logService: LogService,
    private readonly templateService: TemplateService,
    private readonly authService: AuthService,
    private snackBar: MatSnackBar
  ) {
    this.templateService.templateData$().subscribe((template) => {
      this.snackbar = template?.components?.snackbars;
    });
  }

  public preparePage(
    pageTemplate: string,
    params: any,
    htmlPath?: string,
    campaignRewardItemId?: string,
    voucherBasePathOverride?: string
  ): string {
    let page = `${environment.apiUrl}/api/kioskbuilder/voucher`;
    page += `?kioskId=${this.authService.kioskId}`;
    //need to pass the voucherBasePath for Redemption V2, but also keep the old legacy code working for all other voucher types to print correctly
    //page += `&voucherBasePath=pageLinks[${params.pageIndex}].templateSettings[${params.templateIndex}].configurations.voucher`;
    const basePath =
    voucherBasePathOverride
    ?? `pageLinks[${params.pageIndex}].templateSettings[${params.templateIndex}].configurations.voucher`;

    page += `&voucherBasePath=${basePath}`;
  
    if (htmlPath) {
      page += `&htmlPath=${htmlPath}`;
    }

    if (campaignRewardItemId) {
      page += `&campaignRewardItemId=${campaignRewardItemId}`;
    }
    
    if (params.externalIdentifier) {
      page += `&externalIdentifier=${params.externalIdentifier}`;
    }

    page += '&posCodeScale=0.25';

    this.findVariables(pageTemplate).forEach((variable: string) => {
      let data = '';
      switch (variable) {
        case 'nowDate':
        case 'nowDate12h':
          const now = new Date();
          const is12h = variable === 'nowDate12h';
          const format = is12h
            ? 'MM/DD/YYYY hh:mm:ss A'
            : 'MM/DD/YYYY HH:mm:ss';
          data = this.prepareDate(now, format);
          break;
        case 'expiresDateShort':
        case 'expiresDateLong':
        case 'expirationDateShort':
        case 'expirationDateLong':
          const isExpiration =
            variable === 'expirationDateShort' ||
            variable === 'expirationDateLong';
          const date = isExpiration
            ? params.expirationDate
            : params.expiresDate;

          if (date) {
            const isLong =
              variable === 'expiresDateLong' ||
              variable === 'expirationDateLong';
            const parsedDate = moment(date, 'MMM DD, YYYY', 'en');
            const newFormat = isLong ? 'MMMM D, YYYY' : 'MM/DD/YY';

            data = parsedDate.format(newFormat);
          } else {
            data = '';
          }

          break;
        case 'companyName':
          data = `${this.authService.kioskData?.clientName}`;
          break;
        case 'kioskDeviceId':
          data = `${this.authService.preparedKioskDeviceId}`;
          break;
        default:
          data = params[variable];
          break;
      }

      page += `&${variable}=${encodeURIComponent(data)}`;
    });

    uniq(this.findVariables(pageTemplate, true)).forEach((variable: string) => {
      let data = '';
      const regex = /\('([^']+)'\)/;
      const format = variable.match(regex)?.[1];

      if (format?.length) {
        switch (true) {
          case variable.startsWith('nowDate'):
            const now = new Date();

            data = this.prepareDate(now, format);
            break;
          case variable.startsWith('expirationDate'):
          case variable.startsWith('expiresDate'):
            const isExpiration = variable.startsWith('expiration');
            const date = isExpiration
              ? params.expirationDate
              : params.expiresDate;

            if (date) {
              const parsedDate = moment(date, 'MMM DD, YYYY', 'en');

              data = parsedDate.format(format);
            } else {
              data = '';
            }

            break;
        }

        page += `&${variable}=${data}`;
      }
    });

    return page;
  }

  public setIsPrinting(isPrinting: boolean, func: string) {
    this.logService.writeLog(`Set isPrinting from ${this.isPrinting} to ${isPrinting} inside ${func}`, LogType.DEBUG, LogSource.PRINT);
    this.isPrinting = isPrinting;
  }

  public async printTimeOut(time: number): Promise<void> {
    return new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), time)
    );
  }

  private findVariables(pageTemplate: string, isDynamic?: boolean): string[] {
    let variables: string[] = [];
    const variablesArray = isDynamic
      ? TEMPLATE_VARIABLES_DYNAMICS
      : TEMPLATE_VARIABLES;

    variablesArray.forEach((variable: string) => {
      const patternString = `\\{\\{${variable}${isDynamic ? '.*?' : ''}\\}\\}`;
      const regex = new RegExp(patternString, 'g');
      const matches = pageTemplate?.match(regex);

      if (matches) {
        if (isDynamic) {
          variables = [
            ...variables,
            ...matches.map((x) => x.replace('{{', '').replace('}}', '')),
          ];
        } else {
          variables.push(variable);
        }
      }
    });

    return variables;
  }

  private async retryPrint(fn: any, interval = 5000, retriesLeft = 2): Promise<any> {
    try {
      // this.setIsPrinting(true, "Print retry function");
      return await fn();
    } catch (error: any) {
      console.log("retry print error: ", error, this.WebLink.isTimeout);

      // only throw if timeout triggered
      if (!error && this.WebLink.isTimeout) {
        this.logService.writeLog(
          'Log: Print service - timeout detected', LogType.TRACE, LogSource.PRINT
        );

        throw "PRINTFAIL";
      } else {
        this.logService.writeLog(
          'Log: Print service V2 - print retry initiated', LogType.TRACE, LogSource.PRINT
        );
      }

      if (retriesLeft < 0) {
        console.log("I'm done I'm done");
        throw "PRINTFAIL";
      } else {
        // this.snackBar.openFromComponent(SnackbarComponent, {
        //   panelClass: 'error',
        //   duration: 5000,
        //   horizontalPosition: 'end',
        //   data: {
        //     type: 'error',
        //     message: `${this.templateService.templateData.components?.snackbars?.text?.info?.printRetryMessage ?? "Print failed."} Retrying attempt ${3 - (retriesLeft) } out of 3`,
        //   },
        // });
        await new Promise(resolve => setTimeout(resolve, interval));
        return this.retryPrint(fn, interval * 2, retriesLeft - 1);
      }
    }
  }

  public async printPage(printUrl: string): Promise<void> {
    this.printUrl = printUrl;
    console.log(printUrl);
    this.logService.writeLog(printUrl, LogType.DEBUG, LogSource.PRINT);

    console.log("Old printPage activated");

    const timeoutPromise = new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        this.logService.writeLog(
          'Error: OnButton_Connect timed out after 10 seconds', LogType.ERROR, LogSource.PRINT
        );
        resolve();
      }, 10000);
    });

    const connectPromise = this.OnButton_Connect();
    await Promise.race([connectPromise, timeoutPromise])
  }

  // OC-548
  public async printPage_V2(printUrl: string): Promise<void> {
    this.printUrl = printUrl;
    console.log(printUrl);
    this.logService.writeLog(printUrl, LogType.DEBUG, LogSource.PRINT);

    console.log("New printPage activated");
    
    const { multiPrintInterval } = this.templateService.templateData.components?.snackbars?.text?.info;
    const connectPromise = this.retryPrint(async () => {
      await this.OnButton_Connect();
      await this.CheckPrinterStatusAndPrint();
    }, multiPrintInterval ? parseInt(multiPrintInterval) : 5000);

    return await connectPromise;
  }

  private async OnButton_Connect() {
    const self = this;
    self.logService.writeLog(
      'Log: Print service - start connecting to WeblinkClient', LogType.TRACE, LogSource.PRINT
    );
    
    console.log("Weblink before: ", this.WebLink);

    // close weblink function so a new one can take its place
    if (this.WebLink) {
      try {
        self.WebLink.Close();
        self.logService.writeLog(
          `Log: Print service - WebLink connection closed`, LogType.TRACE, LogSource.PRINT
        );
      } catch (error) {
        console.log("Could not close connection", error);
        self.logService.writeLog(
          `Log: Print service - unable to close connection`, LogType.TRACE, LogSource.PRINT
        );
      }
    }

    const { multiTimeoutPeriod } =
      this.templateService.templateData.components?.snackbars?.text?.info;

    this.WebLink = await new WebLinkClient('localhost', 5000, this.isNewPrintVersion);
    this.WebLink.SetTimeoutPeriod(multiTimeoutPeriod ?? 180);
    this.WebLink.AddEventListener(this.OnWebLinkEvent.bind(this));

    console.log("Weblink after: ", this.WebLink);
    console.log("Set multiprint timeout period to: ", this.WebLink.GetTimeoutPeriod())

    this.isConnected = false;
    if (self.isDebugModeEnabled) {
      return new Promise((resolve) => {
        resolve('');
      });
    }
    
    return new Promise<any>((resolve, reject) => {
      new TaskSequence()
        .Start(function () {
          console.log('Getting Kiosk Id');
          self.logService.writeLog(
            'Log: Print service - call weblink method for getting kiosk id', LogType.TRACE, LogSource.PRINT
          );
          self.snackBar.openFromComponent(SnackbarComponent, {
            panelClass: 'info',
            duration: 5000,
            horizontalPosition: 'end',
            data: {
              type: 'info',
              message: self.snackbar?.text?.info?.printMessage?.length
                ? self.snackbar.text.info.printMessage
                : 'Your request is currently in progress. Please wait...',
            },
          });
          return self.WebLink.GetKioskId();
        })
        .Step(function (id: any, error_text: any) {
          if (id == null) {
            console.log('Unable to retrieve Id: ' + error_text);
            self.logService.writeLog(
              'Log: Print service - weblink service return kioskId is null; Error:' +
                error_text, LogType.TRACE, LogSource.PRINT
            );
            self.isConnected = false;
            reject(new Error());
            return;
          }
          self.kioskId = id;

          // add hardcode license value
          // var data = self.Machines[id];
          self.logService.writeLog(
            'Log: Print service - getting printing machine name', LogType.TRACE, LogSource.PRINT
          );
          var data = self.Machines['NRTKIOSK'];

          if (data === undefined) {
            self.logService.writeLog(
              'Log: Print service - printing machine name is undefined', LogType.TRACE, LogSource.PRINT
            );
            self.isConnected = false;
            reject();
            return;
          }

          console.log('Found license:' + JSON.stringify(data));
          self.logService.writeLog(
            'Log: Found license -' + JSON.stringify(data), LogType.TRACE, LogSource.PRINT
          );
          self.logService.writeLog(
            `Log: Print service - starting connection to machine by license key(${data.LicenseKey}) and serial number(${data.SerialNumber})` +
              JSON.stringify(data), LogType.TRACE, LogSource.PRINT
          );
          return self.WebLink.Connect(data.SerialNumber, data.LicenseKey);
        })
        .Step(function (success: any) {
          if (!success) {
            console.log('Failed to connect');
            self.logService.writeLog(
              `Log: Print service - connection to printing machine failed`, LogType.TRACE, LogSource.PRINT
            );
            self.isConnected = false;
            reject('fail to connect');
          }
          self.logService.writeLog(
            `Log: Print service - connection to printing machine established succesfully `, LogType.TRACE, LogSource.PRINT
          );
          resolve('');
        })
        .RunOnce();
    });
  }

  private OnWebLinkEvent(event_class: any, event: any) {
    if (event_class == EventClass.System) {
      if (event) {
        console.log('Connected to CHS');
        this.logService.writeLog(
          `Log: Print service - connection to CHS established succesfully`, LogType.TRACE, LogSource.PRINT
        );
        this.isConnected = true;

        if (!this.isNewPrintVersion) {
          this.OnButton_PrintReceipt(this.printUrl);
        }
      } else {
        console.log('CHS Disconnected');
        this.logService.writeLog(
          `Log: Print service - connection to CHS failed`, LogType.TRACE, LogSource.PRINT
        );
        this.isConnected = false;
      }
    } else {
      console.log('Retrieved Event:' + JSON.stringify(event));
      this.logService.writeLog(
        `Log: Print service - WebLink sreturn an event ${JSON.stringify(
          event
        )}`, LogType.DEBUG, LogSource.PRINT
      );
    }
  }

  public CheckPrinterStatusAndPrint() {
    return new Promise((resolve, reject) => {
      new TaskSequence()
        .Step(() => {
            return this.WebLink.FindDevice("ReceiptPrinter");
        })
        .Step((success: boolean, device: any) => {
          if (success) {
            if (device.Status !== 3) {
              console.log("Device is not ready")
              this.logService.writeLog(
                `Log: Print service V2 - Printer device is not ready, ${device.Status}`, LogType.TRACE, LogSource.PRINT
              );

              this.WebLink.Close();
              reject(false);
            } else {
              console.log("Print begin");
              this.WebLink.PrintReceipt(this.FormatText(this.printUrl, encodeURIComponent(this.kioskId))).Result((success: any, data: any) => {
                if (!success) {
                  console.log('PrintReceipt FAILED V2');
                  this.logService.writeLog(
                    `Log: Print service V2 - WebLink service unable to print the receipt`, LogType.TRACE, LogSource.PRINT
                  );
                  reject(success);
                } else {
                  this.LogFormat('Receipt Printed V2');
                  this.logService.writeLog(
                    `Log: Print service V2 - WebLink service printed receipt succesfully`, LogType.TRACE, LogSource.PRINT
                  );
                  resolve(success);
                }

                if (this.WebLink) {
                  this.WebLink.Close();
                }

                this.logService.writeLog(
                  `Log: Print service V2 - WebLink connection closed`, LogType.TRACE, LogSource.PRINT
                );
              });
            }
          } else {
            this.logService.writeLog(
              `Log: Print service V2 - Printer device has no connection`, LogType.TRACE, LogSource.PRINT
            );
            reject(success);
          }
        })
        .RunOnce();
    })
  }

  private OnButton_PrintReceipt(url: string) {
    const self = this;
    this.WebLink.PrintReceipt(
      self.FormatText(url, encodeURIComponent(self.kioskId))
    ).Result(function (success: any) {
      if (!success) {
        console.log('PrintReceipt FAILED');
        self.logService.writeLog(
          `Log: Print service - WebLink service unable to print the receipt`, LogType.TRACE, LogSource.PRINT
        );
      } else {
        self.LogFormat('Receipt Printed');
        self.logService.writeLog(
          `Log: Print service - WebLink service printed receipt succesfully`, LogType.TRACE, LogSource.PRINT
        );
      }
      self.WebLink.Close();
      self.logService.writeLog(
        `Log: Print service - WebLink connection closed`, LogType.TRACE, LogSource.PRINT
      );
    });
  }

  private FormatText(text: any, vargs: any) {
    text = '' + text;

    var args: any = [];

    if (vargs instanceof Array && arguments.length == 2) args = vargs;
    else for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);

    var parser = /\{([0-9]+)\}/g;

    text = text.replace(parser, function (match: any, name: any) {
      return args[name];
    });

    return text;
  }

  private LogFormat(text: any, vargs?: any) {
    var args = [];

    if (vargs instanceof Array && arguments.length == 2) args = vargs;
    else for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);

    text = this.FormatText(text, args);
    console.log(text);
    this.logService.writeLog('log - logFormat' + text, LogType.TRACE, LogSource.PRINT);
  }

  private prepareDate(date: Date, format: string): string {
    return moment(date).format(format);
  }
}